/**
 * Branding Store
 *
 * Zustand store for tenant branding. The public endpoint populates this
 * before login; localStorage is a speed-up so a returning device paints
 * the last known brand while the network revalidates. Shape is the
 * authenticated `TenantBrandingResponse` so existing readers stay compatible.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TenantBrandingResponse } from '@/config/branding';

interface BrandingState {
  /** Current tenant's branding configuration */
  branding: TenantBrandingResponse | null;

  /** Loading state for branding fetch */
  isLoading: boolean;

  /** Error message if branding fetch failed */
  error: string | null;

  /** Tenant name we've successfully fetched branding for (prevents re-fetches) */
  fetchedForTenant: string | null;

  /**
   * Set the branding configuration and mark as fetched
   */
  setBranding: (branding: TenantBrandingResponse, tenantName: string) => void;

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: string | null) => void;

  /**
   * Clear branding (on logout)
   */
  clearBranding: () => void;
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set) => ({
      branding: null,
      isLoading: false,
      error: null,
      fetchedForTenant: null,

      setBranding: (branding, tenantName) =>
        set({
          branding,
          isLoading: false,
          error: null,
          fetchedForTenant: tenantName,
        }),

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      clearBranding: () =>
        set({
          branding: null,
          error: null,
          isLoading: false,
          fetchedForTenant: null,
        }),
    }),
    {
      name: 'staff-portal-branding',
      // Persist branding and fetchedForTenant for faster loads
      partialize: (state) => ({
        branding: state.branding,
        fetchedForTenant: state.fetchedForTenant,
      }),
    }
  )
);
